import LocationIcon from "@mui/icons-material/LocationOn";
import ScheduleIcon from "@mui/icons-material/Schedule";
import FlightIcon from "@mui/icons-material/FlightTakeoff";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Chip,
} from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { HangarIcon } from "../../../widgets/Icons/HangarIcon";
import { RampIcon } from "../../../widgets/Icons/RampIcon";
import { LocationInfo, TripInfo } from "./TripLocationNavigationDialog";

interface TripLocationNavigationDialogPresenterProps {
  open: boolean;
  onClose: () => void;
  currentLocation: LocationInfo;
  targetLocation: LocationInfo;
  tripInfo: TripInfo;
  onNavigateToTarget: () => void;
  onStayInCurrent: () => void;
  onCancel: () => void;
}

export const TripLocationNavigationDialogPresenter: React.FC<TripLocationNavigationDialogPresenterProps> = ({
  open,
  onClose,
  currentLocation,
  targetLocation,
  tripInfo,
  onNavigateToTarget,
  onStayInCurrent,
  onCancel,
}) => {
  // Format the date range for display
  const formatDateRange = () => {
    const start = format(tripInfo.startTime, "MMM d");
    const end = format(tripInfo.endTime, "MMM d");
    return start === end ? start : `${start} - ${end}`;
  };

  // Get appropriate icon for location type
  const getLocationIcon = (type: string) => {
    switch (type) {
      case 'hangar':
        return <HangarIcon sx={{ fontSize: 20 }} />;
      case 'ramp':
        return <RampIcon sx={{ fontSize: 20 }} />;
      default:
        return <LocationIcon sx={{ fontSize: 20 }} />;
    }
  };

  // Check if target is unassigned
  const isUnassigned = targetLocation.type === 'unassigned';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="trip-location-navigation-dialog"
    >
      <DialogTitle id="trip-location-navigation-dialog">
        <Stack direction="row" alignItems="center" spacing={1}>
          <FlightIcon />
          <Typography variant="h6">Trip at Different Location</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Trip info */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="body1" fontWeight={500}>
                {tripInfo.tailNumber}
              </Typography>
              <Chip
                icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
                label={formatDateRange()}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>

          {/* Location comparison */}
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 2 }}>
            {isUnassigned ? (
              <Stack spacing={2}>
                <Typography variant="body1" color="warning.main">
                  This trip hasn't been assigned to a specific location yet.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You can view the time period when this trip is scheduled, but it doesn't have a hangar or ramp assignment.
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                    TRIP LOCATION
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {getLocationIcon(targetLocation.type)}
                    <Typography variant="body1" fontWeight={500}>
                      {targetLocation.name}
                    </Typography>
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                    YOU'RE VIEWING
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {getLocationIcon(currentLocation.type)}
                    <Typography variant="body1">
                      {currentLocation.name}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            )}
          </Box>

          {/* Explanation text */}
          <Typography variant="body2" color="text.secondary">
            {isUnassigned
              ? "Choose whether to jump to this time period in your current view."
              : "Choose whether to navigate to the trip's location or stay in your current view."}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={onStayInCurrent}
          variant="outlined"
          startIcon={<ScheduleIcon />}
        >
          Stay in {currentLocation.name}
        </Button>
        {!isUnassigned && (
          <Button
            onClick={onNavigateToTarget}
            variant="contained"
            startIcon={<LocationIcon />}
            autoFocus
          >
            Go to {targetLocation.name}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};